import { useMemo, useState } from "react";
import { format, parse } from "date-fns";
import { AttributeTypes } from "@iddataweb/idw-common-ui/enums";
import { MONTH_DAY_YEAR_INPUT_DATE_FORMAT } from "@constants/date-formats";
import { DATE_ATTRIBUTE_TYPE_FIELD_IDs, DOB_ATTRIBUTE_TYPE_FIELD_IDs } from "@constants/self-service-constants";
import { LabelWidthTypes } from "@enums/label-width-types";
import { FormattedRpServiceConfigurationAttributeWithFields } from "@idw-types/data/rp-service-configuration";
import { DateInputFormGroup } from "@containers/common/date-input-form-group/date-input-form-group";
import styles from "./prefilled-attribute-date-component.less";

type PrefilledAttributeDateComponentProps = {
    attributeTypesWithField: FormattedRpServiceConfigurationAttributeWithFields;
    attributeFieldValues: { [key: string]: string };
    attributeRequired: boolean;
    onAttributeFieldChange: (changeObject: { [key: string]: { [key: string]: string } }) => void;
};

export const PrefilledAttributeDateComponent = (props: PrefilledAttributeDateComponentProps) => {
    const {
        attributeTypesWithField,
        attributeFieldValues,
        attributeRequired,
        onAttributeFieldChange
    } = props;

    const [typedDate, setTypedDate] = useState("");

    const fieldIds = useMemo(() => {
        if (attributeTypesWithField.attributeType.name === AttributeTypes.DOB) {
            return DOB_ATTRIBUTE_TYPE_FIELD_IDs;
        } else {
            return DATE_ATTRIBUTE_TYPE_FIELD_IDs;
        }
    }, [attributeTypesWithField.attributeType.name]);

    const handleDateChange = (newDate: Date) => {
        const value = format(newDate, MONTH_DAY_YEAR_INPUT_DATE_FORMAT)
        const newDateAttributeFields = {
            [fieldIds.month]: value?.slice(0, 2) || "",
            [fieldIds.day]: value?.slice(3, 5) || "",
            [fieldIds.year]: value?.slice(6, 10) || "",
        };
        onAttributeFieldChange({
            [attributeTypesWithField.attributeType.id]: newDateAttributeFields
        });
        setTypedDate(value);
    };

    const handleDateChangeRaw = (dateString: string) => {
        const month = dateString.slice(0, 2);
        const day = dateString.slice(3, 5);
        const year = dateString.slice(6, 10);

        onAttributeFieldChange({
            [attributeTypesWithField.attributeType.id]: {
                [fieldIds.month]: month,
                [fieldIds.day]: day,
                [fieldIds.year]: year,
            }
        });

        if (((typedDate.length === 1 && dateString.length === 2) ||
            (typedDate.length === 4 && dateString.length === 5))) {
            setTypedDate(dateString + "/");
        } else if (dateString.length === 0) {
            setTypedDate("");
        } else if ((typedDate.length < dateString.length && dateString[dateString.length - 1] === "/" && typedDate[typedDate.length - 1] === "/")) {
            setTypedDate(typedDate);
        } else {
            setTypedDate(dateString);
        }
    };

    const dateValue: Date | null = useMemo(() => {
        let result = null;

        if (attributeFieldValues) {
            if ([AttributeTypes.DOB, AttributeTypes.DATE].includes(attributeTypesWithField.attributeType.name)) {
                if (attributeFieldValues[fieldIds.month]
                    && attributeFieldValues[fieldIds.year]
                    && attributeFieldValues[fieldIds.day]) {
                    const {
                        [fieldIds.month]: month,
                        [fieldIds.day]: day,
                        [fieldIds.year]: year,
                    } = attributeFieldValues;

                    const value = `${month}/${day}/${year}`;
                    result = parse(value, MONTH_DAY_YEAR_INPUT_DATE_FORMAT, new Date());
                }
            }
        }

        return result;

    }, [attributeFieldValues, attributeTypesWithField.attributeType.name]);

    return (
        <DateInputFormGroup
            controlId={attributeTypesWithField.id}
            automationId={attributeTypesWithField.id}
            labelWidth={LabelWidthTypes.MEDIUM}
            label={attributeTypesWithField.attributeLabel.label}
            ariaLabel={attributeTypesWithField.attributeLabel.label}
            className={styles.prefilledAttributeDateInput}
            placeholder={MONTH_DAY_YEAR_INPUT_DATE_FORMAT.toLocaleUpperCase()}
            disabled={false}
            isRequired={attributeRequired}
            minDate={new Date(1900, 0, 1)}
            maxDate={new Date()}
            dateFormat={MONTH_DAY_YEAR_INPUT_DATE_FORMAT}
            typedDate={typedDate}
            value={dateValue}
            onChange={handleDateChange}
            onChangeRawDateOnly={handleDateChangeRaw}
            shouldCloseOnSelect={true}
            showMonthDropdown={true}
            showYearDropdown={true}
            showMonthYearPicker={false}
            isClearable={false}
        />
    );
};